import { mouseDistanceMet } from '../../api/tapi';

function defaultOptions(options) {
  return Object.assign(
    {
      enabled: true,
      distance2trigger: 1,
      onMoveStart: () => {},
      onMove: () => {},
      onMoveEnd: () => {},
      raf: true,
    },
    options
  );
}

/**
 * Move abstraction
 * @note
 * directive should be attached to an element that remains in DOM during the movement
 * and not drops from it as a side effect
 *
 * @note
 * reserved `state` properties:
 * DOM events: eUp, eDown, eMove
 * flags: started, ended, canceled
 *
 * @example
 * v-tm-move="{
 *   enabled: boolean
 *   distance2trigger: 8
 *   onMoveStart
 *   onMove
 *   onMoveEnd
 *   raf: boolean // use requestAnimationFrame for onMove event processing
 * }"
 */
export default {
  bind: function (el, binding, vnode) {
    el._value = defaultOptions(binding.value);
    el._onMousedown = binding.def._onMousedown.bind(el);
    el._onMousemove = binding.def._onMousemove.bind(el);
    el._onMouseup = binding.def._onMouseup.bind(el);
    el._onKeyboard = binding.def._onKeyboard.bind(el);
  },

  inserted(el, binding, vnode) {
    el.addEventListener('mousedown', el._onMousedown);
  },

  update(el, binding, vnode) {
    el._value = defaultOptions(binding.value);
  },

  unbind(el, binding, vnode) {
    el._value = null;
    el._state = null;
    el.removeEventListener('mousedown', el._onMousedown);
    document.removeEventListener('mousemove', el._onMousemove);
    document.removeEventListener('mouseup', el._onMouseup);
    document.removeEventListener('keydown', el._onKeyboard);
  },

  // /// ///// //////// ///////////// ///////////////////// //////////////////////////////////

  _onMousedown(eDown) {
    if (0 === eDown.button) {
      this._state = {
        eDown: eDown,
        el: this,
        started: false,
      };

      const nonDraggable = eDown.target.closest('.v-tm-move-disable');

      if (this._value.enabled && !nonDraggable) {
        // @note: handle eDown in case if v-tm-move is a child of another v-tm-move
        eDown.stopPropagation();
        document.addEventListener('mousemove', this._onMousemove);
        document.addEventListener('mouseup', this._onMouseup);
      }
    }
  },

  _onMousemove(eMove) {
    this._state.eMove = eMove;

    if (
      !this._state.started &&
      mouseDistanceMet(this._state.eDown, eMove, this._value.distance2trigger)
    ) {
      this._state.started = true;
      this._value.onMoveStart(this._state);
      document.addEventListener('keydown', this._onKeyboard);
    }

    if (this._state.started) {
      if (this._value.raf) {
        this._state._animationFrame = requestAnimationFrame(() => {
          if (this._state && !this._state.ended) {
            this._value.onMove(this._state);
          }
        });
      } else {
        this._value.onMove(this._state);
      }
    }
  },

  _onMouseup(eUp) {
    eUp.stopPropagation();
    document.removeEventListener('mousemove', this._onMousemove);
    document.removeEventListener('mouseup', this._onMouseup);
    document.removeEventListener('keydown', this._onKeyboard);
    if (this._state.started) {
      this._state.ended = true;
      this._state.eUp = eUp;
      if (this._state._animationFrame) {
        cancelAnimationFrame(this._state._animationFrame);
        this._state._animationFrame = null;
      }
      this._value.onMoveEnd(this._state);
    }
    this._state = null;
  },

  _onKeyboard(e) {
    if ('Escape' === e.key) {
      document.removeEventListener('mousemove', this._onMousemove);
      document.removeEventListener('keydown', this._onKeyboard);
      this._state.canceled = true;
    }
  },
};
